import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { AppDispatch, RootState } from "../../../store";
import { fetchRepairReplaceReturnEquipmentCatalog } from "../../../store/thunks/submitTicketThunk";
import {
  addRepairReplaceReturnEquipmentLine,
  addRepairReplaceReturnEquipmentScan,
  addRepairReplaceReturnEquipmentSerials,
  EquipmentUnitCondition,
  addRepairReplaceReturnEquipmentUnits,
  insertRepairReplaceReturnEquipmentLineAfter,
  removeAllRepairReplaceReturnEquipmentLines,
  removeRepairReplaceReturnEquipmentLine,
  removeRepairReplaceReturnEquipmentUnit,
  RepairReplaceReturnEquipmentLine,
  RepairReplaceReturnEquipmentUnit,
  updateRepairReplaceReturnEquipmentLine,
  updateRepairReplaceReturnEquipmentUnit,
} from "../../../store/slices/ticketingPortalSlice";
import {
  EQUIPMENT_VENDORS,
  EquipmentCatalogItem,
  getEquipmentScopeForSubCategory,
} from "../data/repairReplaceReturnEquipmentCatalog";

const useRepairReplaceReturnEquipment = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { subCategory, catalog, knownItems, catalogLoading, lines } = useSelector((state: RootState) => ({
    subCategory: state.ticketingPortal.submitTicketDraft.subCategory,
    catalog: state.ticketingPortal.repairReplaceReturnEquipmentCatalog,
    knownItems: state.ticketingPortal.repairReplaceReturnEquipmentKnownItems,
    catalogLoading: state.ticketingPortal.repairReplaceReturnEquipmentCatalogLoading,
    lines: state.ticketingPortal.repairReplaceReturnEquipmentLines,
  }));

  // Fetch catalog when sub-category changes
  useEffect(() => {
    if (subCategory) {
      dispatch(fetchRepairReplaceReturnEquipmentCatalog(subCategory));
    }
  }, [subCategory, dispatch]);

  // When API returns devices for a sub-category, they are already filtered
  // by the backend to be appropriate. Don't apply additional client-side filtering.
  // The static scope filter is only needed as a fallback for the static EQUIPMENT_CATALOG.
  const scopedCatalog = useMemo(
    () => catalog, // Use catalog as-is since API already filters by sub-category
    [catalog]
  );

  // Lines added under earlier sub-categories must keep resolving after the catalog switches,
  // so look up against every device loaded so far (current catalog wins).
  const catalogBySku = useMemo(() => {
    const map: Record<string, EquipmentCatalogItem> = { ...knownItems };
    catalog.forEach((item) => {
      map[item.sku] = item;
    });
    return map;
  }, [catalog, knownItems]);

  return {
    subCategory,
    catalogLoading,
    scopedCatalog,
    vendors: EQUIPMENT_VENDORS,
    getCatalogItem: (sku: string) => catalogBySku[sku],
    lines,
    addLine: (sku?: string) => dispatch(addRepairReplaceReturnEquipmentLine(sku)),
    insertLineAfter: (afterId: string, sku: string, withUnit: boolean) =>
      dispatch(insertRepairReplaceReturnEquipmentLineAfter({ afterId, sku, withUnit })),
    updateLine: (id: string, patch: Partial<Omit<RepairReplaceReturnEquipmentLine, "id">>) =>
      dispatch(updateRepairReplaceReturnEquipmentLine({ id, patch })),
    addScannedUnit: (sku: string, capture: "serial" | "quantity", serial: string, condition: EquipmentUnitCondition) =>
      dispatch(addRepairReplaceReturnEquipmentScan({ sku, capture, serial, condition })),
    addSerialsBulk: (groups: Array<{ sku: string; serials: string[]; condition: EquipmentUnitCondition }>) =>
      dispatch(addRepairReplaceReturnEquipmentSerials({ groups })),
    addUnits: (lineId: string, count: number) => dispatch(addRepairReplaceReturnEquipmentUnits({ lineId, count })),
    updateUnit: (lineId: string, unitId: string, patch: Partial<Omit<RepairReplaceReturnEquipmentUnit, "id">>) =>
      dispatch(updateRepairReplaceReturnEquipmentUnit({ lineId, unitId, patch })),
    removeUnit: (lineId: string, unitId: string) =>
      dispatch(removeRepairReplaceReturnEquipmentUnit({ lineId, unitId })),
    removeLine: (id: string) => dispatch(removeRepairReplaceReturnEquipmentLine(id)),
    removeAllLines: () => dispatch(removeAllRepairReplaceReturnEquipmentLines()),
  };
};

export default useRepairReplaceReturnEquipment;
